// Logica (sin React) del editor de propiedades: a que recurso de Terraform corresponde un objeto, que ayuda hay para cada atributo,
// validar un valor y agregar/quitar propiedades. Las propiedades viven en data.props como pares [clave, valor] (todo texto).
import MAPA from "modAws/tfMapa.json";
import { CATALOGO, COMUNES, SOLO_LECTURA, RESUMEN } from "modAws/propiedadesAws";

const INVERSO = {};
Object.entries(MAPA).forEach(([tipo, [servicio, objeto]]) => { if (!INVERSO[`${servicio}|${objeto}`]) INVERSO[`${servicio}|${objeto}`] = tipo; });

// Tipo de recurso de Terraform de un nodo ("aws_instance"), o null si no se puede saber.
export const tipoRecurso = (nodo) => {
    const d = (nodo && nodo.data) || {};
    const m = d.tf && /(aws_[a-z0-9_]+)\.[^.\/]+$/.exec(d.tf);
    if (m) return m[1];
    if (nodo.type === "grupo") return d.tipo === "vpc" ? "aws_vpc" : /^subnet/.test(d.tipo || "") ? "aws_subnet" : null;
    return INVERSO[`${d.servicio}|${d.objeto}`] || null;
};

// "metadata_options[0].http_tokens" -> "metadata_options.http_tokens"
export const normalizarClave = (k) => String(k).replace(/\[\d+\]/g, "");
export const esExpresion = (v) => String(v == null ? "" : v).includes("‹");

export const docUrl = (tipo) => (tipo ? `https://registry.terraform.io/providers/hashicorp/aws/latest/docs/resources/${(CATALOGO[tipo] && CATALOGO[tipo].doc) || tipo.replace(/^aws_/, "")}` : null);
export const tituloTipo = (tipo) => (tipo && CATALOGO[tipo] ? CATALOGO[tipo].titulo : null);

// Ayuda de un atributo, o null si no esta catalogado (se puede editar igual, sin ayuda).
export const ayudaDe = (tipo, clave) => {
    const k = normalizarClave(clave);
    if (SOLO_LECTURA[k]) return { tipo: "solo-lectura", titulo: k, descripcion: SOLO_LECTURA[k], clave: k };
    const cat = tipo && CATALOGO[tipo];
    if (cat && cat.atributos[k]) return { ...cat.atributos[k], clave: k };
    if (COMUNES[k]) return { ...COMUNES[k], clave: k };
    if (k.startsWith("tags.")) return { tipo: "texto", titulo: `Etiqueta ${k.slice(5)}`, descripcion: "Etiqueta (par clave-valor) para organizar el recurso: filtrar, asignar costos o dar permisos.", clave: k };
    return null;
};

// Atributos del catalogo que el objeto todavia no tiene.
export const disponibles = (tipo, props) => {
    const puestas = new Set(props.map(([k]) => normalizarClave(k)));
    const salida = [];
    const cat = tipo && CATALOGO[tipo];
    if (cat) Object.entries(cat.atributos).forEach(([clave, a]) => { if (!puestas.has(clave)) salida.push({ clave, ...a }); });
    Object.entries(COMUNES).forEach(([clave, a]) => { if (!puestas.has(clave)) salida.push({ clave, ...a }); });
    return salida;
};

// Valor con el que se agrega un atributo nuevo: el recomendado, o el primero; en numericos el minimo o el default.
export const valorInicial = (ayuda) => {
    if (ayuda.valores && ayuda.valores.length) { const r = ayuda.valores.find(x => x.rec === "ok") || ayuda.valores[0]; return r.v; }
    if (ayuda.tipo === "num") {
        // el ejemplo (un valor sensato) antes que el default: p. ej. el default de respaldos es 0, que los desactiva
        const ej = Number(ayuda.ejemplo);
        if (ayuda.ejemplo !== undefined && Number.isFinite(ej)) return String(ej);
        const d = Number(String(ayuda.defecto || "").split(/[ (]/)[0]);
        return String(Number.isFinite(d) && ayuda.defecto ? d : (ayuda.min !== undefined ? ayuda.min : 0));
    }
    return ayuda.ejemplo || "";
};

export const valorDe = (props, clave) => { const p = props.find(([k]) => k === clave); return p ? p[1] : undefined; };
export const ponerProp = (props, clave, valor) => (props.some(([k]) => k === clave) ? props.map(([k, v]) => (k === clave ? [k, String(valor)] : [k, v])) : [...props, [clave, String(valor)]]);
export const quitarProp = (props, clave) => props.filter(([k]) => k !== clave);

// Que significa el valor actual (entre los valores o sugerencias del catalogo).
export const explicarValor = (ayuda, valor) => {
    if (!ayuda) return null;
    return [...(ayuda.valores || []), ...(ayuda.sugerencias || [])].find(x => String(x.v) === String(valor)) || null;
};

// { nivel: "ok" | "aviso" | "error", mensaje }
export const validarValor = (ayuda, valor) => {
    const v = String(valor == null ? "" : valor).trim();
    if (!ayuda || esExpresion(v)) return { nivel: "ok", mensaje: "" };
    if (ayuda.tipo === "bool" && v !== "true" && v !== "false") return { nivel: "error", mensaje: "Debe ser true o false." };
    if (ayuda.tipo === "num") {
        if (v === "") return { nivel: "error", mensaje: "Escribe un número." };
        const n = Number(v);
        if (!Number.isFinite(n)) return { nivel: "error", mensaje: "No es un número." };
        if (ayuda.min !== undefined && n < ayuda.min) return { nivel: "error", mensaje: `El mínimo es ${ayuda.min}${ayuda.unidad ? " " + ayuda.unidad : ""}.` };
        if (ayuda.max !== undefined && n > ayuda.max) return { nivel: "error", mensaje: `El máximo es ${ayuda.max}${ayuda.unidad ? " " + ayuda.unidad : ""}.` };
    }
    if (ayuda.tipo === "enum" && !ayuda.libre && v !== "" && !ayuda.valores.some(x => x.v === v))
        return { nivel: "aviso", mensaje: "Este valor no está entre los conocidos; revisa que sea válido para el recurso." };
    return { nivel: "ok", mensaje: "" };
};

// Resumen que se ve bajo el nombre del objeto; undefined si este tipo no tiene (para no borrar el que ya traia).
export const resumenDe = (tipo, props) => {
    const claves = tipo && RESUMEN[tipo];
    if (!claves) return undefined;
    const partes = claves.map(c => valorDe(props, c)).filter(v => v !== undefined && v !== "" && !esExpresion(v));
    return partes.join(" · ");
};

// Aplica un cambio de propiedad a un nodo (lo usan "Corregir" de la Revision y la edicion de varios objetos a la vez):
// devuelve el nodo con la propiedad puesta (o quitada si valor es null), marcada como editada y con el resumen recalculado.
export const aplicarCambioProp = (nodo, clave, valor) => {
    const props0 = (nodo.data && nodo.data.props) || [];
    const props = valor === null ? quitarProp(props0, clave) : ponerProp(props0, clave, valor);
    const editadas = valor === null ? (nodo.data.propsEditadas || []).filter(k => k !== clave) : [...new Set([...(nodo.data.propsEditadas || []), clave])];
    const resumen = resumenDe(tipoRecurso(nodo), props);
    return { ...nodo, data: { ...nodo.data, props, propsEditadas: editadas, ...(resumen !== undefined ? { resumen } : {}) } };
};
